/// <amd-module name="Repos/Views/CommitHub/CommitViewService" />
import { DefaultDiscussionFilterOption } from "Repos/Artifact/Discussion/ArtifactDiscussionThreadService";
import { BranchStats, IChangeWithDiscussionThread, PullRequestStats } from "Repos/Changes/Contracts";
import { getChangesCollection } from "Repos/Changes/Util";
import { IChangesActionsService } from "Repos/ChangesActions/Contracts";
import { getGitNavigationService } from "Repos/ChangesCommon/ChangesNavigationService";
import { IChange, IChangeCollection, IChangesViewService } from "Repos/ChangesCommon/Contracts";
import { ICommitActionsService } from "Repos/CommitActions/Contracts";
import { GitCommitTagRef, ICommitsDataProviderService } from "Repos/CommitsList/CommitsDataProviderService";
import {
    GitAnnotatedTag,
    GitPullRequest,
    GitPullRequestQuery,
    GitPullRequestQueryType,
    GitPush,
    GitQueryCommitsCriteria,
    GitRefUpdate,
    GitRepository,
    GitStatus,
    GitUserDate,
    GitVersionDescriptor,
    GitVersionOptions,
    GitVersionType,
    VersionControlChangeType
} from "Repos/Common/Contracts/Git";
import { ICommit } from "Repos/Common/Contracts/History";
import { IReposActionService } from "Repos/Common/ReposActionService";
import { getReposRoutingService, IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { getShortCommitComment } from "Repos/Common/Util/Commit";
import { getIconNameForFile } from "Repos/Common/Util/File";
import { branchNameToRef, EMPTY_OBJECT_ID, getShortCommitId, refToBranchName, refToTagName } from "Repos/Common/Util/Ref";
import {
    branchToVersionDescriptor,
    commitToVersionDescriptor,
    getVersionString,
    gitVersionStringToVersionDescriptor,
    tagToVersionDescriptor
} from "Repos/Common/Util/Version";
import { IVCViewModelDataProviderService } from "Repos/Common/VCViewModelDataProviderService";
import { IChangesTreeDiscussionIntegration, IDiffSummaryDiscussionIntegration } from "Repos/Discussion/Common/DiscussionContracts";
import { addCommentToFileItem, addCommentToRootItem } from "Repos/Discussion/Editor/Utils/CommentAction";
import { IGitFilesService } from "Repos/Files/GitFilesService";
import { ICreateBranchDialogProps, ICreateTagDialogProps } from "Repos/VersionDialogs/Dialogs.Props";
import * as Resources from "Repos/Views/CommitHub/Resources";
import {
    IObservableValue,
    IReadonlyObservableArray,
    IReadonlyObservableValue,
    ObservableArray,
    ObservableCollection,
    ObservableValue
} from "VSS/Core/Observable";
import { caseInsensitiveContains, format } from "VSS/Core/Util/String";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { addMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { IUnsavedChangesService } from "VSS/Features/PlatformUI/UnsavedChangesService";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { FastPageSwitch, onClickFPS } from "VSS/Platform/FPS";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IMenuItem } from "VSSUI/Menu";
import { IBasicIdentityRef } from "VSSUI/Persona";
import { Filter, IFilterState } from "VSSUI/Utilities/Filter";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { ITreeItem } from "VSSUI/Utilities/TreeItemProvider";
import { getCommitData, GitCommitChange } from "./CommitData";
import { CommitPermissions, hasPermission, setPermissions } from "./CommitPermissionService";
import { CommitRoleItem, CommitPullRequest, CommitTag } from "./Contracts";
import { getCommitDiscussionService } from "./Discussion/CommitDiscussionThreadService";
import { getGitClient } from "./RestClient/Git";

export namespace FilesFilterKeys {
    export const keyword = "keyword";
    export const discussions = "discussions";
}

export interface ICommitParent {
    changes?: GitCommitChange[];
    commit: IObservableValue<ICommit | undefined>;
    displayCommitId: string;
    displayName: string;
    hasMore: boolean;
    id: string;
    isInitialCommit?: boolean;
    isMergeSummary?: boolean;
    parentCommitId: string;
}

interface IDiffCommitsData {
    allChangesIncluded: boolean;
    changeCounts: { [changeType: number]: number };
    changes: GitCommitChange[];
    commitId: { full: string; short: string };
}

export interface ICommitViewService extends IChangesViewService, IVssService {
    /**
     * Get the path of all files changed in this commit (for the selected parent).
     */
    getAllChangedPaths(): string[];

    /**
     * Gets the author of the commit.
     */
    getAuthor(): GitUserDate;

    /**
     * Gets the branch stats of the commit.
     */
    getBranchStats(): IReadonlyObservableValue<BranchStats | undefined>;

    /**
     * Gets the changes associated with commit (for the selected parent).
     */
    getChanges(): IReadonlyObservableValue<IChangeCollection<GitVersionDescriptor>>;

    /**
     * Gets the committer of the change
     */
    getCommitter(): GitUserDate;

    /**
     * Gets the commit id currently loaded.
     */
    getCommitId(): string;

    /**
     * Gets the commit message for currently loaded commit.
     */
    getCommitMessage(): string;

    /**
     * Gets the filter store for the files in the commit.
     */
    getFilesFilter(): Filter;

    /**
     * Gets url to file hub for specified tag
     */
    getFileUrlForTag(name: string): string;

    /**
     * Gets the menu items which appear in the header.
     *
     */
    getHeaderMenuItems(smallScreen: boolean): IReadonlyObservableArray<IHeaderCommandBarItem>;

    /**
     * Gets the menu items which for each item in tree.
     *
     */
    getItemMenuItems(
        item: IChange,
        treeDiffIntegration?: IChangesTreeDiscussionIntegration<ITreeItem<IChangeWithDiscussionThread>>,
        rootDiffIntegration?: IDiffSummaryDiscussionIntegration
    ): IReadonlyObservableArray<IMenuItem>;

    /**
     * Gets all options for the parent commits to compare to
     */
    getParents(): ICommitParent[];

    /**
     * Gets the prs associated with the commit
     */
    getPullRequests(): IReadonlyObservableArray<CommitPullRequest>;

    /**
     * Gets pull request for specified id
     */
    getPullRequestUrl(id: number): string;

    /**
     * Gets repository object associated with request
     */
    getRepository(): GitRepository;

    /**
     * Gets actions assoicated with the commit.(Author, Commuiter, Pusher)
     */
    getRoleItems(): ArrayItemProvider<CommitRoleItem>;

    /**
     * Gets the id of the selected ICommitParent entry
     */
    getSelectedParentId(): IReadonlyObservableValue<string>;

    /**
     * Gets the currently selected tab (Files or Details)
     */
    getSelectedTabId(): IReadonlyObservableValue<string>;

    /**
     * Gets the currently selected thread.
     */
     getSelectedThreadId(): IReadonlyObservableValue<number | undefined>;

    /**
     * Gets the short commit message for currently loaded commit.
     */
    getShortCommitMessage(): string;

    /**
     * Gets the pipeline statuses for the commit
     */
    getStatuses(): IReadonlyObservableArray<GitStatus>;

    /**
     * Gets tags for the commit
     */
    getTags(): IReadonlyObservableArray<CommitTag>;

    /**
     * Gets the currently selected git version descriptor
     */
    getVersion(): GitVersionDescriptor;

    /**
     * Indicates if commit data was successfully found.
     */
    isCommitValid(): boolean;

    /**
     * Ensure that parent commit details have started to load
     */
    loadParentCommitDetails(): void;

    /**
     * Sets the id of the parent to compare to
     *
     * @param parentId Id of the parent to select
     */
    setSelectedParentId(parentId: string): void;

    /**
     * Sets the selected tab (Files or Details)
     *
     * @param tabId Newly selected tab
     */
    setSelectedTabId(tabId: string): void;

    getCommitTooManyChanges () : boolean;
}

const diffCommitsDataProviderId = "ms.vss-code-web.diff-commits-data-provider";
const changesBatchSize = 1000;

class CommitHubViewService extends VssService implements ICommitViewService {
    private author: GitUserDate;
    private branchStats: ObservableValue<BranchStats | undefined> | undefined;
    private changes: ObservableValue<IChangeCollection<GitVersionDescriptor>>;
    private commiter: GitUserDate;
    private commitFound: boolean = true;
    private commitId: string;
    private commitMessage: string;
    private defaultParentId = "summary";
    private fetchedParentCommits = false;
    private filesFilter = new Filter();
    private parents: ICommitParent[] = [];
    private pullRequests: ObservableArray<CommitPullRequest> | undefined;
    private push: GitPush;
    private refName: string;
    private repository: GitRepository;
    private selectedParentId = new ObservableValue("");
    private selectedTabId: ObservableValue<string>;
    private selectedThreadId: ObservableValue<number | undefined>;
    private statuses: ObservableArray<GitStatus> | undefined;
    private tags: ObservableArray<CommitTag> | undefined;
    private version: GitVersionDescriptor;
    private commitTooManyChanges: boolean = false;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const vcViewModelDataProviderService = this.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        this.repository = vcViewModelDataProviderService.getRepository()!;

        const pageStateHistoryService = this.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");

        const detailsData = getCommitData(this.pageContext);

        if (detailsData && detailsData.commitDetails) {
            this.commitId = detailsData.commitDetails.commitId as string;
            this.version = commitToVersionDescriptor(this.commitId);
            this.commitMessage = detailsData.commitDetails.comment;
            this.author = detailsData.commitDetails.author;
            this.commiter = detailsData.commitDetails.committer;
            this.push = detailsData.commitDetails.push;
            this.commitTooManyChanges = detailsData.commitDetails.commitTooManyChanges;

            let changes: IChangeCollection<GitVersionDescriptor> | undefined;

            if (detailsData.commitDetails.parents && detailsData.commitDetails.parents.length > 1) {
                this.defaultParentId = "diffparent1";
                this.parents.push(
                    ...detailsData.commitDetails.parents.map((parentCommitId, i) => {
                        const id = "diffparent" + (i + 1);
                        const parent: ICommitParent = {
                            commit: new ObservableValue(undefined),
                            displayCommitId: parentCommitId,
                            displayName: format(Resources.ParentFormat, i + 1),
                            hasMore: false,
                            id,
                            parentCommitId
                        };
                        return parent;
                    })
                );
            }

            if (detailsData.diffParentDetails && detailsData.diffParentDetails.diffParent) {
                this.parents.forEach(p => {
                    if (p.id === detailsData.diffParentDetails!.parentId) {
                        p.changes = detailsData.diffParentDetails!.diffParent.changes || [];
                        changes = this.processChanges(p.changes, p.parentCommitId, true);
                        this.selectedParentId.value = p.id;
                    }
                });
            }

            if (detailsData.commitDetails.changes) {
                const isInitialCommit = !detailsData.commitDetails.parents || detailsData.commitDetails.parents.length === 0;
                const summaryParentId = isInitialCommit ? EMPTY_OBJECT_ID : detailsData.commitDetails.parents[0];
                const isMergeSummary = this.parents.length > 0;
                this.parents.push({
                    changes: detailsData.commitDetails.changes,
                    commit: new ObservableValue(undefined),
                    displayCommitId: isMergeSummary ? this.commitId : summaryParentId,
                    displayName: isMergeSummary ? Resources.ChangesWhileMerging : format(Resources.ParentFormat, 1),
                    hasMore: !detailsData.allChangesIncluded,
                    id: "summary",
                    isInitialCommit,
                    isMergeSummary,
                    parentCommitId: summaryParentId
                });

                if (!this.selectedParentId.value) {
                    changes = this.processChanges(detailsData.commitDetails.changes, summaryParentId, detailsData.allChangesIncluded);
                    this.selectedParentId.value = "summary";
                }
            }

            this.changes = new ObservableValue(changes || this.processChanges([], "", true));

            const navigationService = getGitNavigationService(this.pageContext);
            navigationService.setChanges(this.changes);

            const historyState = pageStateHistoryService.getProjectedState();
            const path = historyState["path"] || "/";
            this.refName = historyState["refName"];

            let selectedItem = this.getItem(path);
            if (selectedItem.isInvalid) {
                // if invalid, just load root node.
                selectedItem = this.getItem("/");
                pageStateHistoryService.queueValueChange("path", "", HistoryBehavior.replace);
            }

            this.selectedThreadId = new ObservableValue(parseInt(historyState["discussionId"]) || undefined);
            navigationService.setSelectedItem(selectedItem);

            this.filesFilter.setFilterItemState(FilesFilterKeys.discussions, { value: [DefaultDiscussionFilterOption.id] });
            this.filesFilter.setDefaultState(this.filesFilter.getState());
            this.filesFilter.subscribe(this.applyFilesFilter);

            const selectedThreadItem: IReadonlyObservableValue<IChange> = navigationService.getSelectedItem();
            selectedThreadItem.subscribe((value: IChange): void => {
                const change: IChangeWithDiscussionThread = value as IChangeWithDiscussionThread;
                if (change.thread) {
                    if (this.selectedThreadId.value !== change.thread.id) {
                        this.selectedThreadId.value = change.thread.id;
                    }
                } else {
                    if (this.selectedThreadId.value) {
                        this.selectedThreadId.value = undefined;
                    }
                }
            });

            pageStateHistoryService.registerObservable<IChange>(
                navigationService.getSelectedItem(),
                "path",
                HistoryBehavior.newEntry,
                item => (item.path === "/" ? "" : item.path),
                path => {
                    // Selected item path changed from the URL. Ensure that
                    // we fully process the change by calling setSelectedItem.
                    let item = this.getItem(path || "/");
                    if (item.isInvalid) {
                        item = this.getItem("/");
                    }
                    navigationService.setSelectedItem(item);
                    return item;
                }
            );

            pageStateHistoryService.registerObservable<string>(
                this.selectedParentId,
                "_a",
                HistoryBehavior.newEntry,
                parentId => (parentId === this.defaultParentId ? "" : parentId),
                queryValue => {
                    let parentId = this.defaultParentId;
                    if (queryValue && this.parents.find(p => p.id === queryValue)) {
                        parentId = queryValue;
                    }
                    this.setSelectedParentId(parentId);
                    return parentId;
                }
            );
        } else {
            this.commitFound = false;
            this.changes = new ObservableValue(this.processChanges([], "", true));
        }

        const historyState = pageStateHistoryService.getProjectedState();
        this.selectedTabId = new ObservableValue(historyState["tab"] || "files");

        pageStateHistoryService.registerObservable<string>(
            this.selectedTabId,
            "tab",
            HistoryBehavior.newEntry,
            selectedTabId => (selectedTabId === "files" ? "" : selectedTabId),
            queryParam => queryParam || "files"
        );

        pageStateHistoryService.registerObservable<number | undefined>(
            this.selectedThreadId,
            "discussionId",
            HistoryBehavior.newEntry,
            id => (id ? id.toFixed() : ""),
            queryParam => parseInt(queryParam) || undefined
        );

        // Set commit permissions using the repository information
        setPermissions(pageContext, this.repository);

        this.pageContext.getService<IUnsavedChangesService>("IUnsavedChangesService").register({
            isDirty: () => getCommitDiscussionService(this.pageContext).hasUnsavedThreads(),
            getDirtyMessage: () => Resources.UnsavedCommentsMessage
        });
    }

    public isCommitValid(): boolean {
        return this.commitFound;
    }

    public getCommitTooManyChanges () : boolean {
        return this.commitTooManyChanges;
    }

    public getAuthor(): GitUserDate {
        return this.author;
    }

    public getBranchStats(): IReadonlyObservableValue<BranchStats | undefined> {
        if (!this.branchStats) {
            this.branchStats = new ObservableValue<BranchStats | undefined>(undefined);

            let branchFullName = this.refName;
            if (!branchFullName) {
                branchFullName = this.repository.defaultBranch;
            }

            const branch = refToBranchName(branchFullName);
            getGitClient(this.pageContext)
                .getCommitsBatch(
                    {
                        $top: 1,
                        itemVersion: {
                            version: branch,
                            versionType: GitVersionType.Branch,
                            versionOptions: GitVersionOptions.None
                        },
                        compareVersion: {
                            version: this.commitId,
                            versionType: GitVersionType.Commit,
                            versionOptions: GitVersionOptions.None
                        }
                    } as GitQueryCommitsCriteria,
                    this.repository.id,
                    this.repository.project.id
                )
                .then(commits => {
                    if (commits.length === 0) {
                        const reposRoutingService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");
                        const browseFileUrl = reposRoutingService.getFilesUrl(
                            this.repository.name,
                            this.repository.project.name,
                            undefined,
                            branchToVersionDescriptor(branch)
                        );

                        this.branchStats!.value = { name: branch, url: browseFileUrl };
                        this.updateBranchPullRequest();
                    }
                });

            // Start loading the PRs for the commit
            this.getPullRequests();
        }
        return this.branchStats;
    }

    public getRoleItems(): ArrayItemProvider<CommitRoleItem> {
        const items: CommitRoleItem[] = [];
        items.push({
            role: Resources.Authored,
            date: this.author.date,
            identity: this.createPersona(this.author)
        });
        items.push({
            role: Resources.Committed,
            date: this.commiter.date,
            identity: this.createPersona(this.commiter)
        });

        items.push({
            role: Resources.Pushed,
            date: this.push.date,
            identity: { id: this.push.pushedBy.id, displayName: this.push.pushedBy.displayName, imageUrl: this.push.pushedBy.imageUrl }
        });

        return new ArrayItemProvider(items);
    }

    public getAllChangedPaths(): string[] {
        const parent = this.parents.find(p => p.id === this.selectedParentId.value);
        return parent && parent.changes
            ? parent.changes
                  .filter(change => !change.item.isFolder)
                  .map(change => change.item.path || change.item.serverItem || change.sourceServerItem || "")
            : [];
    }

    public getChanges(): IReadonlyObservableValue<IChangeCollection<GitVersionDescriptor>> {
        return this.changes;
    }

    public getCommitter(): GitUserDate {
        return this.commiter;
    }

    public getCommitId(): string {
        return this.commitId;
    }

    public getCommitMessage(): string {
        return this.commitMessage;
    }

    public getCompareHeaderMenuItems(item: IChange, rootDiffIntegration?: IDiffSummaryDiscussionIntegration): IMenuItem[] | undefined {
        if (hasPermission(this.pageContext, CommitPermissions.AddEditComment) && rootDiffIntegration && this.isRootItem(item)) {
            return [addCommentToRootItem(rootDiffIntegration, Resources.AddCommentToCommit, false)];
        }
    }

    public getFilesFilter(): Filter {
        return this.filesFilter;
    }

    public getFileUrlForTag(tag: string): string {
        const routeService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");
        return routeService.getFilesUrl(this.repository.name, this.repository.project.name, undefined, tagToVersionDescriptor(refToTagName(tag)));
    }

    public getHeaderMenuItems(smallScreen: boolean): IReadonlyObservableArray<IHeaderCommandBarItem> {
        const { pageContext } = this;

        const menuItems: IHeaderCommandBarItem[] = [];
        const reposRoutingService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");

        const canCreateBranch = hasPermission(pageContext, CommitPermissions.CreateBranch);
        const canCreateTag = hasPermission(pageContext, CommitPermissions.CreateTag);

        const browseFileUrl = reposRoutingService.getFilesUrl(
            this.repository.name,
            this.repository.project.name,
            undefined,
            commitToVersionDescriptor(this.commitId)
        );

        menuItems.push({
            id: "browse-files",
            groupKey: "default-actions",
            text: Resources.BrowseFiles,
            href: browseFileUrl,
            important: !smallScreen
        });

        if(userHasClaim(this.pageContext, UserClaims.Member)) {
            menuItems.push({
                id: "search-branches",
                groupKey: "default-actions",
                text: Resources.SearchInBranches,
                onActivate: () => {
                    this.getCommitActionsService().then(actionsService => {
                        actionsService.searchForCommit(this.commitId, this.repository);
                    });
                },
                important: false
            });
        }

        if (canCreateBranch) {
            menuItems.push({
                id: "create-cherry-pick",
                iconProps: { iconName: "BranchShelveset" },
                text: Resources.CherryPick,
                groupKey: "modify-actions",
                onActivate: () => {
                    this.getChangesActionsService().then(actionsService => {
                        actionsService.cherryPickCommit(this.commitId, this.repository);
                    });
                },
                important: false
            });

            menuItems.push({
                id: "create-revert",
                iconProps: { iconName: "Undo" },
                text: Resources.Revert,
                groupKey: "modify-actions",
                onActivate: () => {
                    this.getChangesActionsService().then(actionsService => {
                        const branchStats = this.getBranchStats();
                        let targetBranch = undefined;
                        if (branchStats && branchStats.value && branchStats.value.name) {
                            targetBranch = branchStats.value.name;
                        }

                        actionsService.revertCommit(this.commitId, this.repository, targetBranch);
                    });
                },
                important: false
            });
        }

        if (canCreateTag) {
            menuItems.push({
                id: "create-tag",
                iconProps: { iconName: "Tag" },
                text: Resources.CreateTag,
                groupKey: "create-actions",
                onActivate: () => {
                    const reposActionService: IReposActionService = pageContext.getService<IReposActionService>("IReposActionService");
                    reposActionService.createTag({
                        referenceVersion: commitToVersionDescriptor(this.commitId),
                        repository: this.repository,
                        onComplete: (createdTag: GitAnnotatedTag) => {
                            const routingService = pageContext.getService<IReposRoutingService>("IReposRoutingService");
                            const fileUrl = routingService.getFilesUrl(
                                this.repository.name,
                                this.repository.project.name,
                                undefined,
                                tagToVersionDescriptor(refToBranchName(createdTag.name))
                            );

                            FastPageSwitch(pageContext, fileUrl, true);
                        }
                    } as ICreateTagDialogProps);
                },
                important: false
            });
        }

        if (canCreateBranch) {
            menuItems.push({
                id: "create-branch",
                iconProps: { iconName: "OpenSource" },
                text: Resources.CreateBranch,
                groupKey: "create-actions",
                onActivate: () => {
                    const reposActionService: IReposActionService = pageContext.getService<IReposActionService>("IReposActionService");
                    reposActionService.createBranch({
                        referenceVersion: commitToVersionDescriptor(this.commitId),
                        repository: this.repository,
                        onComplete: (createdBranch: GitRefUpdate) => {
                            const routingService = pageContext.getService<IReposRoutingService>("IReposRoutingService");
                            const fileUrl = routingService.getFilesUrl(
                                this.repository.name,
                                this.repository.project.name,
                                undefined,
                                branchToVersionDescriptor(refToBranchName(createdBranch.name))
                            );

                            FastPageSwitch(pageContext, fileUrl, true);
                        }
                    } as ICreateBranchDialogProps);
                },
                important: false
            });
        }

        const menuItemCollection = new ObservableCollection<IHeaderCommandBarItem>();
        menuItemCollection.push(menuItems);

        const commitListService = pageContext.getService<ICommitsDataProviderService>("ICommitsDataProviderService");
        const navigationService = getGitNavigationService(this.pageContext);
        const selectedItem = navigationService.getSelectedItem();

        addMenuItems(
            pageContext,
            menuItemCollection,
            "ms.vss-code-web.git-commit-details-menu",
            commitListService.getCommitContributionContext(
                this.commitId,
                this.commitMessage,
                false,
                this.repository.id,
                selectedItem && selectedItem.value && selectedItem.value.path
            ),
            {
                processExternalMenuItem: (m, c) => {
                    (m as IHeaderCommandBarItem).important = (c as any).important === true;
                    return m;
                }
            }
        );

        return menuItemCollection;
    }

    public getItemMenuItems(
        item: IChange,
        treeDiffIntegration?: IChangesTreeDiscussionIntegration<ITreeItem<IChangeWithDiscussionThread>>,
        rootDiffIntegration?: IDiffSummaryDiscussionIntegration
    ): IReadonlyObservableArray<IMenuItem> {
        const { pageContext } = this;

        const filesService = pageContext.getService<IGitFilesService>("IGitFilesService");
        const reposRoutingService = pageContext.getService<IReposRoutingService>("IReposRoutingService");
        const navigationService = getGitNavigationService(this.pageContext);

        const collection = new ObservableCollection<IMenuItem>();

        // Add fixed menu items based on the item type
        const menuItems: IMenuItem[] = [];

        menuItems.push({
            groupKey: "file-items",
            iconProps: { iconName: "Page" },
            id: "view",
            href: reposRoutingService.getFilesUrl(
                this.repository.name,
                this.repository.project.name,
                item.path,
                commitToVersionDescriptor(item.commitId)
            ),
            onActivate: (menuItem, event) => {
                return onClickFPS(pageContext, menuItem.href!, true, event);
            },
            text: Resources.ViewInFileExplorer
        });

        if (!item.isFolder) {
            menuItems.push({
                groupKey: "file-items",
                iconProps: { iconName: "History" },
                id: "view-history",
                href: reposRoutingService.getFilesHistoryUrl(
                    this.repository.name,
                    this.repository.project.name,
                    item.path,
                    commitToVersionDescriptor(item.commitId)
                ),
                onActivate: (menuItem, event) => {
                    return onClickFPS(pageContext, menuItem.href!, true, event);
                },
                text: Resources.ViewHistory
            });

            menuItems.push({
                groupKey: "file-items",
                iconProps: { iconName: "BranchCompare" },
                id: "compare-previous",
                href: reposRoutingService.getFilesCompareUrl(
                    this.repository.name,
                    this.repository.project.name,
                    item.path,
                    commitToVersionDescriptor(item.commitId),
                    gitVersionStringToVersionDescriptor("PGC" + item.commitId),
                    commitToVersionDescriptor(item.commitId)
                ),
                onActivate: (menuItem, event) => {
                    return onClickFPS(pageContext, menuItem.href!, true, event);
                },
                text: Resources.CompareToPreviousVersion
            });

            menuItems.push({
                groupKey: "file-actions",
                iconProps: { iconName: "Download" },
                id: "file-download",
                onActivate: () => {
                    const href = filesService.getFileDownloadUrl(item.path, gitVersionStringToVersionDescriptor("GC" + item.commitId),true);
                    window.open(href, "_blank", "noopener");
                },
                text: Resources.Download
            });

            if (hasPermission(pageContext, CommitPermissions.AddEditComment) && treeDiffIntegration) {
                menuItems.push(addCommentToFileItem(treeDiffIntegration, item.path, () => navigationService.setSelectedItem(item)));
            }
        }

        if (hasPermission(pageContext, CommitPermissions.AddEditComment) && rootDiffIntegration && this.isRootItem(item)) {
            menuItems.push(
                addCommentToRootItem(rootDiffIntegration, Resources.AddCommentToCommit, true, () => navigationService.setSelectedItem(item))
            );
        }

        collection.push(menuItems);

        return collection;
    }

    public getParents(): ICommitParent[] {
        return this.parents;
    }

    public getPullRequests(): IReadonlyObservableArray<CommitPullRequest> {
        if (!this.pullRequests) {
            this.pullRequests = new ObservableArray([]);

            getGitClient(this.pageContext)
                .getPullRequestQuery(
                    {
                        queries: [
                            {
                                type: GitPullRequestQueryType.LastMergeCommit,
                                items: [this.commitId]
                            },
                            {
                                type: GitPullRequestQueryType.Commit,
                                items: [this.commitId]
                            }
                        ]
                    } as GitPullRequestQuery,
                    this.repository.id,
                    this.repository.project.id
                )
                .then(queryResults => {
                    const mergePrArray = (queryResults.results[0] || {})[this.commitId];
                    const commitPrArray = (queryResults.results[1] || {})[this.commitId];

                    if (mergePrArray && mergePrArray.length > 0) {
                        mergePrArray.sort((pr1, pr2) => {
                            return pr2.creationDate.valueOf() - pr1.creationDate.valueOf();
                        });
                    }

                    if (commitPrArray && commitPrArray.length > 0) {
                        commitPrArray.sort((pr1, pr2) => {
                            return pr2.creationDate.valueOf() - pr1.creationDate.valueOf();
                        });
                    }
                    let allPullRequests: GitPullRequest[] | undefined;

                    if (mergePrArray && mergePrArray.length > 0) {
                        if (commitPrArray && commitPrArray.length > 0) {
                            allPullRequests = mergePrArray.concat(commitPrArray);
                        } else {
                            allPullRequests = mergePrArray;
                        }
                    } else if (commitPrArray && commitPrArray.length > 0) {
                        allPullRequests = commitPrArray;
                    }

                    if (allPullRequests && allPullRequests.length > 0) {
                        this.pullRequests!.push(
                            ...allPullRequests.map((value: GitPullRequest) => {
                                return {
                                    ...value,
                                    linkProps: {
                                        href: this.getPullRequestUrl(value.pullRequestId)
                                    }
                                };
                            })
                        );

                        this.updateBranchPullRequest();
                    }
                });
        }
        return this.pullRequests;
    }

    public getRepository(): GitRepository {
        return this.repository;
    }

    public getPullRequestUrl(id: number): string {
        const routeService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");
        return routeService.getPullRequestUrl(this.repository.name, this.repository.project.name, id);
    }

    public getSelectedParentId(): IReadonlyObservableValue<string> {
        return this.selectedParentId;
    }

    public getSelectedTabId(): IReadonlyObservableValue<string> {
        return this.selectedTabId;
    }

    public getSelectedThreadId(): IReadonlyObservableValue<number | undefined> {
        return this.selectedThreadId;
    }

    public getShortCommitMessage(): string {
        return getShortCommitComment(this.commitMessage, 80, true);
    }

    public getStatuses(): IReadonlyObservableArray<GitStatus> {
        if (!this.statuses) {
            this.statuses = new ObservableArray([]);

            getGitClient(this.pageContext)
                .getStatuses(this.commitId, this.repository.id, this.repository.project.id, undefined, undefined, true)
                .then(statuses => {
                    if (statuses) {
                        this.statuses!.push(...statuses);
                    }
                });
        }
        return this.statuses;
    }

    public getTags(): IReadonlyObservableArray<CommitTag> {
        if (!this.tags) {
            this.tags = new ObservableArray([]);

            const commitsDataService = this.pageContext.getService<ICommitsDataProviderService>("ICommitsDataProviderService");

            commitsDataService.fetchTags(this.commitId).then(tags => {
                if (tags) {
                    this.tags!.push(
                        ...tags.map((value: GitCommitTagRef) => {
                            return {
                                ...value,
                                linkProps: {
                                    href: this.getFileUrlForTag(value.name)
                                }
                            };
                        })
                    );
                }
            });
        }
        return this.tags;
    }

    public getVersion(): GitVersionDescriptor {
        return this.version;
    }

    public isRootItem(item: IChange): boolean {
        const rootItem = this.changes.value.rootTreeItem.data;
        return item === rootItem;
    }

    public loadParentCommitDetails(): void {
        if (!this.fetchedParentCommits) {
            this.fetchedParentCommits = true;
            if (this.parents.length) {
                getGitClient(this.pageContext)
                    .getCommitsBatch(
                        { ids: this.parents.map(p => p.displayCommitId) } as GitQueryCommitsCriteria,
                        this.repository.id,
                        this.repository.project.id
                    )
                    .then(commits => {
                        if (commits) {
                            const routingService = getReposRoutingService(this.pageContext);
                            this.parents.forEach((parent, index) => {
                                const commit = commits[index];
                                if (commit) {
                                    parent.commit.value = {
                                        comment: commit.comment,
                                        commit,
                                        linkProps: {
                                            href: routingService.getCommitUrl(this.repository.name, this.repository.project.name, commit.commitId)
                                        },
                                        repositoryId: this.repository.id,
                                        shortCommitId: parent.displayName + " - " + getShortCommitId(commit.commitId)
                                    };
                                }
                            });
                        }
                    });
            }
        }
    }

    public setSelectedParentId(parentId: string): void {
        if (parentId !== this.selectedParentId.value) {
            this.selectedParentId.value = parentId;

            const parent = this.parents.find(p => p.id === parentId);
            if (parent && parent.changes) {
                this.changes.value = this.processChanges(parent.changes, parent.parentCommitId, !parent.hasMore);
            } else {
                // Reset changes to an empty set
                this.changes.value = this.processChanges([], "", true);
                if (parent) {
                    this.fetchChangesForParent(parent, 0, changesBatchSize);
                }
            }
        }
    }

    public setSelectedTabId(tabId: string): void {
        this.selectedTabId.value = tabId;
    }

    private createPersona(gitUser: GitUserDate): IBasicIdentityRef {
        return { id: gitUser.email, displayName: gitUser.name, imageUrl: gitUser.imageUrl };
    }

    private async fetchChangesForParent(parent: ICommitParent, skipCount: number, maxNumberOfChanges: number): Promise<void> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const parentVersion = commitToVersionDescriptor(parent.parentCommitId);
        contributionService
            .getDataAsync<IDiffCommitsData>(diffCommitsDataProviderId, {
                baseVersion: getVersionString(parentVersion),
                maxNumberOfChanges,
                repositoryId: this.repository.id,
                skipCount,
                targetVersion: getVersionString(commitToVersionDescriptor(this.commitId))
            })
            .then(data => {
                // Apply the changes if we are still looking at the same parent as
                // when we started fetching the diff.
                if (data && this.selectedParentId.value === parent.id) {
                    parent.changes = skipCount > 0 && parent.changes ? [...parent.changes, ...data.changes] : data.changes;
                    parent.hasMore = !data.allChangesIncluded;

                    this.changes.value = this.processChanges(
                        parent.changes,
                        parent.parentCommitId,
                        data.allChangesIncluded,
                        skipCount + changesBatchSize
                    );
                }
            });
    }

    private async getChangesActionsService(): Promise<IChangesActionsService> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return (await contributionService.getService<IChangesActionsService>("ms.vss-code-web.changes-actions-service"))!;
    }

    private async getCommitActionsService(): Promise<ICommitActionsService> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return (await contributionService.getService<ICommitActionsService>("ms.vss-code-web.commit-actions-service"))!;
    }

    private getItem(path: string): IChange {
        let item = this.changes.value.changesByPath[path];
        if (!item) {
            // Look for the item ignoring casing
            item = this.changes.value.changesByPath[path.toLocaleLowerCase()];
            if (!item) {
                // Look for folder
                const treeItem = this.changes.value.treeItemsByPath[path];
                if (treeItem) {
                    item = treeItem.data;
                }

                if (!item) {
                    // Item does not exist. Create a entry for the invalid item
                    const invalidItem = {
                        iconProps: { iconName: getIconNameForFile(path) },
                        isFolder: false,
                        isInvalid: true,
                        isRepo: false,
                        folder: path,
                        path: path,
                        name: path,
                        commitId: this.commitId,
                        changeType: VersionControlChangeType.None,
                        objectId: undefined
                    };
                    item = invalidItem;
                }
            }
        }
        return item;
    }

    private processChanges(
        changes: GitCommitChange[],
        parentCommitId: string,
        allChangesIncluded: boolean,
        nextSkip?: number
    ): IChangeCollection<GitVersionDescriptor> {
        const keywordFilterItemValue = this.filesFilter.getFilterItemValue<string>(FilesFilterKeys.keyword);
        const filteredChanges = keywordFilterItemValue
            ? changes.filter(c => caseInsensitiveContains(c.item.path || c.item.serverItem || c.sourceServerItem || "", keywordFilterItemValue))
            : changes;

        const parentId = this.selectedParentId.value || this.defaultParentId;
        return getChangesCollection(
            filteredChanges.map(c => {
                return {
                    changeType: c.changeType,
                    commitId: c.commitId,
                    item: {
                        isFolder: c.item.isFolder,
                        isSymLink: c.item.isSymLink,
                        originalPath: c.sourceServerItem || c.item.serverItem,
                        path: c.item.path || c.item.serverItem!
                    }
                };
            }),
            this.repository.name,
            this.commitId,
            commitToVersionDescriptor(this.commitId),
            commitToVersionDescriptor(parentCommitId),
            allChangesIncluded
                ? undefined
                : () => {
                      const parent = this.parents.find(p => p.id === parentId);
                      if (parent && (this.selectedParentId.value || this.defaultParentId) === parentId) {
                          this.fetchChangesForParent(parent, nextSkip || changesBatchSize, changesBatchSize);
                      }
                  },
            `${this.commitId}.${parentCommitId}.${changes.length}.${keywordFilterItemValue}`
        );
    }

    private updateBranchPullRequest(): void {
        if (this.branchStats && this.branchStats.value && this.pullRequests && this.pullRequests.length) {
            const branchRefName = branchNameToRef(this.branchStats.value.name);
            for (const pr of this.pullRequests.value) {
                if (pr.targetRefName === branchRefName) {
                    const reposRoutingService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");
                    const prStats: PullRequestStats = {
                        id: pr.pullRequestId.toString(),
                        title: pr.title,
                        url: reposRoutingService.getPullRequestUrl(this.repository.name, this.repository.project.name, pr.pullRequestId)
                    };

                    this.branchStats.value = { ...this.branchStats.value, associatedPRStats: prStats };
                    break;
                }
            }
        }
    }

    private applyFilesFilter = (filterState: IFilterState, action: string) => {
        if (action === "filter-applied") {
            if (filterState[FilesFilterKeys.discussions] && filterState[FilesFilterKeys.discussions]!.value.length) {
                getCommitDiscussionService(this.pageContext).setFilter(filterState[FilesFilterKeys.discussions]!.value[0]);
            }

            if (FilesFilterKeys.keyword in filterState) {
                const parent = this.parents.find(p => p.id === this.selectedParentId.value);
                if (parent && parent.changes) {
                    this.changes.value = this.processChanges(parent.changes, parent.parentCommitId, !parent.hasMore);
                }
            }
        }
    };
}

Services.add("ICommitViewService", { serviceFactory: CommitHubViewService });
